import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  Image,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useQuery } from "convex/react";
import { api } from "../convex/_generated/api";
import { useAuthData } from "../hooks/useAuthData";

const C = {
  bg: "#F9F5F3",
  ink: "#2d1a12",
  inkMuted: "#6b5346",
  accent: "#4d2c1d",
  line: "rgba(77, 44, 29, 0.12)",
  card: "#FFFFFF",
  pill: "rgba(77, 44, 29, 0.06)",
};

function formatRelativeDay(ts) {
  const d = Math.floor((Date.now() - ts) / (24 * 60 * 60 * 1000));
  if (d <= 0) return "Today";
  if (d === 1) return "Yesterday";
  return `${d} days ago`;
}

/**
 * Full-screen recap list: bites that still need a star rating (newest first).
 * Opened from MapScreen / MapScreen.web inside a React Native `Modal`, or embedded in the web desktop sidebar (`embedded`).
 */
export default function RecapMomentScreen({
  navigation,
  onNavigateToRateMoment,
  embedded,
  onEmbeddedBack,
}) {
  const { authData } = useAuthData();
  const userPrivyDid = authData?.privyDid;

  const rows = useQuery(
    api.moments.getMomentsForRecap,
    userPrivyDid ? { privyDid: userPrivyDid } : "skip"
  );

  const goBack = () => {
    if (embedded && typeof onEmbeddedBack === "function") {
      onEmbeddedBack();
      return;
    }
    navigation?.goBack?.();
  };
  const handleSelectMoment = (momentId) => {
    if (onNavigateToRateMoment) {
      onNavigateToRateMoment(momentId);
      return;
    }
    navigation?.navigate?.("RecapRateMoment", { momentId });
  };

  const loading = rows === undefined;
  const list = rows ?? [];

  const Root = embedded ? View : SafeAreaView;
  const rootProps = embedded
    ? { style: [styles.safe, styles.safeEmbedded] }
    : { style: styles.safe, edges: ["top", "left", "right", "bottom"] };

  return (
    <Root {...rootProps}>
      {/* Header */}
      <View style={[styles.header, embedded && styles.headerEmbedded]}>
        <TouchableOpacity
          onPress={goBack}
          style={styles.backBtn}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          accessibilityRole="button"
          accessibilityLabel="Back"
        >
          <Ionicons name="chevron-back" size={24} color={C.accent} />
        </TouchableOpacity>

        <View style={styles.headerCenter}>
          <Text style={[styles.title, embedded && styles.titleEmbedded]} numberOfLines={1}>
            Recap
          </Text>
          <Text style={[styles.subtitle, embedded && styles.subtitleEmbedded]} numberOfLines={1}>
            Rate your bites
          </Text>
        </View>

        <View style={styles.headerRight} />
      </View>

      {!userPrivyDid ? (
        <View style={styles.centered}>
          <View style={styles.emptyIconWrap}>
            <Ionicons name="sparkles-outline" size={36} color={C.accent} />
          </View>
          <Text style={styles.emptyTitle}>Sign in to see your recaps</Text>
          <Text style={styles.emptyBody}>
            Bites you haven&apos;t rated yet will show up here so you can finish them.
          </Text>
          <TouchableOpacity style={styles.primaryBtn} onPress={goBack} activeOpacity={0.88}>
            <Text style={styles.primaryBtnText}>Go back</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.flex1}>
          <View style={styles.hintRow}>
            <Ionicons name="information-circle-outline" size={18} color={C.inkMuted} />
            <Text style={styles.hintText}>
              Tap a bite to rate it — then you can edit photos, caption, or tags.
            </Text>
          </View>

          <ScrollView
            style={styles.flex1}
            contentContainerStyle={[styles.scrollContent, embedded && styles.scrollContentEmbedded]}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            {loading ? (
              <View style={styles.loadingWrap}>
                <ActivityIndicator size="large" color={C.accent} />
                <Text style={styles.loadingLabel}>Loading moments…</Text>
              </View>
            ) : list.length === 0 ? (
              <View style={styles.emptyCard}>
                <View style={styles.emptyCardIcon}>
                  <Ionicons name="restaurant-outline" size={32} color={C.accent} />
                </View>
                <Text style={styles.emptyCardTitle}>You&apos;re all caught up</Text>
                <Text style={styles.emptyCardBody}>
                  When you check in without a rating, the bite appears here until you rate it.
                </Text>
              </View>
            ) : (
              <View style={styles.list}>
                {list.map((row) => {
                  const thumbs = row.photoThumbUrls ?? [];
                  const count = row.photoCount ?? 0;
                  const firstUrl = thumbs[0];
                  const moreExtra = count > 1 ? count - 1 : 0;

                  return (
                  <TouchableOpacity
                    key={row._id}
                    style={styles.row}
                    activeOpacity={0.82}
                    onPress={() => handleSelectMoment(row._id)}
                  >
                    <View style={styles.rowAccent} />
                    <View style={styles.thumbCol}>
                      {firstUrl ? (
                        <View style={styles.thumbFrame}>
                          <Image
                            source={{ uri: firstUrl }}
                            style={styles.thumbImage}
                            resizeMode="cover"
                          />
                          {moreExtra > 0 ? (
                            <View style={styles.thumbBadge}>
                              <Text style={styles.thumbBadgeText}>+{moreExtra}</Text>
                            </View>
                          ) : null}
                        </View>
                      ) : (
                        <View style={[styles.thumbFrame, styles.thumbPlaceholder]}>
                          <Image
                            source={require("../assets/DisgoBrownLogo.png")}
                            style={styles.thumbLogo}
                            resizeMode="contain"
                          />
                        </View>
                      )}
                    </View>
                    <View style={styles.rowBody}>
                      <View style={styles.rowTop}>
                        <Text style={styles.rowName} numberOfLines={2}>
                          {row.restaurantName}
                        </Text>
                        <Ionicons name="chevron-forward" size={20} color={C.inkMuted} />
                      </View>
                      <View style={styles.metaRow}>
                        <View style={styles.metaItem}>
                          <Ionicons name="time-outline" size={15} color={C.inkMuted} />
                          <Text style={styles.metaText}>{formatRelativeDay(row.createdAt)}</Text>
                        </View>
                        <View style={styles.dot} />
                        <View style={styles.metaItem}>
                          <Ionicons name="star-outline" size={14} color="#B45309" />
                          <Text style={styles.metaStrong}>Add rating</Text>
                        </View>
                        <View style={styles.dot} />
                        <View
                          style={[
                            styles.visPill,
                            row.visibility === "public"
                              ? styles.visPublic
                              : row.visibility === "only_me"
                                ? styles.visOnlyMe
                                : styles.visFriends,
                          ]}
                        >
                          <Text
                            style={[
                              styles.visText,
                              row.visibility === "public"
                                ? styles.visTextPub
                                : row.visibility === "only_me"
                                  ? styles.visTextOnly
                                  : styles.visTextFr,
                            ]}
                          >
                            {row.visibility === "public"
                              ? "Public"
                              : row.visibility === "only_me"
                                ? "Only me"
                                : "Friends"}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </TouchableOpacity>
                  );
                })}
              </View>
            )}
          </ScrollView>
        </View>
      )}
    </Root>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: C.bg,
  },
  safeEmbedded: {
    minHeight: 0,
  },
  flex1: { flex: 1 },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingBottom: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: C.line,
  },
  headerEmbedded: {
    paddingBottom: 8,
  },
  titleEmbedded: {
    fontSize: 22,
  },
  subtitleEmbedded: {
    fontSize: 13,
    marginTop: 0,
  },
  scrollContentEmbedded: {
    paddingHorizontal: 14,
    paddingBottom: 20,
  },
  backBtn: {
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 22,
  },
  headerCenter: {
    flex: 1,
    alignItems: "center",
    paddingHorizontal: 8,
  },
  headerRight: { width: 44 },
  title: {
    fontSize: 28,
    fontFamily: "Arvo_700Bold",
    color: C.ink,
    letterSpacing: -0.5,
  },
  subtitle: {
    marginTop: 2,
    fontSize: 14,
    fontFamily: "AbhayaLibre_400Regular",
    color: C.inkMuted,
  },
  hintRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    paddingHorizontal: 20,
    paddingTop: 14,
    paddingBottom: 10,
  },
  hintText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    fontFamily: "AbhayaLibre_400Regular",
    color: C.inkMuted,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 6,
    paddingBottom: 28,
  },
  loadingWrap: {
    paddingVertical: 48,
    alignItems: "center",
  },
  loadingLabel: {
    marginTop: 12,
    fontSize: 16,
    fontFamily: "AbhayaLibre_400Regular",
    color: C.inkMuted,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 32,
  },
  emptyIconWrap: {
    width: 88,
    height: 88,
    borderRadius: 44,
    backgroundColor: C.pill,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  emptyTitle: {
    fontSize: 24,
    fontFamily: "AbhayaLibre_700Bold",
    color: C.ink,
    textAlign: "center",
    marginBottom: 10,
  },
  emptyBody: {
    fontSize: 17,
    lineHeight: 24,
    fontFamily: "AbhayaLibre_400Regular",
    color: C.inkMuted,
    textAlign: "center",
    marginBottom: 28,
  },
  primaryBtn: {
    backgroundColor: C.accent,
    paddingHorizontal: 28,
    paddingVertical: 14,
    borderRadius: 999,
  },
  primaryBtnText: {
    color: "#fff",
    fontSize: 17,
    fontFamily: "AbhayaLibre_700Bold",
  },
  emptyCard: {
    backgroundColor: C.card,
    borderRadius: 20,
    padding: 28,
    alignItems: "center",
    borderWidth: 1,
    borderColor: C.line,
    shadowColor: "#2d1a12",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 2,
  },
  emptyCardIcon: {
    width: 64,
    height: 64,
    borderRadius: 20,
    backgroundColor: C.pill,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  emptyCardTitle: {
    fontSize: 22,
    fontFamily: "AbhayaLibre_700Bold",
    color: C.ink,
    marginBottom: 8,
    textAlign: "center",
  },
  emptyCardBody: {
    fontSize: 16,
    lineHeight: 23,
    fontFamily: "AbhayaLibre_400Regular",
    color: C.inkMuted,
    textAlign: "center",
  },
  list: {
    gap: 12,
  },
  row: {
    flexDirection: "row",
    backgroundColor: C.card,
    borderRadius: 18,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: C.line,
    shadowColor: "#2d1a12",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 1,
  },
  rowAccent: {
    width: 4,
    backgroundColor: C.accent,
    opacity: 0.85,
  },
  thumbCol: {
    justifyContent: "center",
    paddingVertical: 10,
    paddingLeft: 10,
  },
  thumbFrame: {
    width: 72,
    height: 72,
    borderRadius: 14,
    overflow: "hidden",
    backgroundColor: C.pill,
  },
  thumbImage: {
    width: "100%",
    height: "100%",
  },
  thumbPlaceholder: {
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: C.line,
    backgroundColor: "#faf8f6",
  },
  thumbLogo: {
    width: "78%",
    height: "78%",
  },
  thumbBadge: {
    position: "absolute",
    right: 6,
    bottom: 6,
    backgroundColor: "rgba(45, 26, 18, 0.88)",
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 8,
  },
  thumbBadgeText: {
    color: "#fff",
    fontSize: 12,
    fontFamily: "AbhayaLibre_700Bold",
  },
  rowBody: {
    flex: 1,
    paddingVertical: 12,
    paddingRight: 12,
    paddingLeft: 10,
    minWidth: 0,
  },
  rowTop: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  rowName: {
    flex: 1,
    fontSize: 19,
    fontFamily: "AbhayaLibre_700Bold",
    color: C.ink,
    lineHeight: 24,
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: 10,
    gap: 8,
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  metaText: {
    fontSize: 14,
    fontFamily: "AbhayaLibre_400Regular",
    color: C.inkMuted,
  },
  metaStrong: {
    fontSize: 14,
    fontFamily: "AbhayaLibre_700Bold",
    color: C.ink,
  },
  dot: {
    width: 3,
    height: 3,
    borderRadius: 2,
    backgroundColor: C.inkMuted,
    opacity: 0.45,
  },
  visPill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
  },
  visPublic: {
    backgroundColor: "rgba(77, 44, 29, 0.08)",
  },
  visFriends: {
    backgroundColor: "rgba(180, 83, 9, 0.12)",
  },
  visOnlyMe: {
    backgroundColor: "rgba(55, 65, 81, 0.12)",
  },
  visText: {
    fontSize: 12,
    fontFamily: "AbhayaLibre_700Bold",
  },
  visTextPub: {
    color: C.accent,
  },
  visTextFr: {
    color: "#92400e",
  },
  visTextOnly: {
    color: "#374151",
  },
});
